import * as React from 'react';

import * as moment from 'moment';

import { Alert, Grid, Spacer, TextField, Typography } from '@emburse/ui-core';

import { AddableUserAutocomplete } from '@ReactComponents/Shared/Autocomplete';

import { ICardsGatewayIssuingModalValues, LoadingState } from '@Actions/cardsGatewayIssuingModalActions';

import { IParentCardOption } from '@ClientApi/cardIssuing';
import { IUserExtension } from '@ClientModels/userExtension';
import { AccountCapabilities, CardType } from '@Shared/interfaces/CardsGatewayInterfaces';
import { ICardRequestModel, IUserProfileModel } from '@Shared/models';

import { isManagerFlagCheck } from '../../../../utils/userProfileRoleHelper';
import { CancellableDateRange } from '../Fields/CancellableDateRange';
import { CapabilitiesIntervalField } from '../Fields/CapabilitiesIntervalField';
import { CurrencyInput } from '../Fields/CurrencyInput';
import { EmployeeSelect } from '../Fields/EmployeeSelect';
import { MerchantCategoryCodesField } from '../Fields/MerchantCategoryCodesField';
import { ParentCardSelect } from '../Fields/ParentCardSelect';
import { VendorRestrictionsField } from '../Fields/VendorRestrictionsField';

interface IIssuingDetailsFormProps {
  // Component props
  capabilities: AccountCapabilities; // Object detailing what fields the card program actually supports
  linkedAccountId?: string; // Only defined if editing
  institutionName?: string; // Only used in virtual card issuing
  cardRequestId?: string; // Only used in card request popup
  // Options
  userOptions: IUserExtension[];
  usersById: Record<string, IUserExtension>;
  parentCardOptions: IParentCardOption[];
  parentCardsById: Record<string, IParentCardOption>;
  userProfile: IUserProfileModel;

  // Values
  values: ICardsGatewayIssuingModalValues;
  // Handler
  onChange: (update: Partial<ICardsGatewayIssuingModalValues>) => void;
  loading: LoadingState;
  showAsDrawer?: boolean;
  isInEditMode?: boolean;
  multiple: boolean;
}

export class IssuingDetailsForm extends React.Component<IIssuingDetailsFormProps> {
  onChange = (update: Partial<ICardsGatewayIssuingModalValues>) => {
    this.props.onChange(update);
  };

  isManager = isManagerFlagCheck(this.props.userProfile);
  // TODO: Move these a level up
  // isInEditMode = () => Boolean(this.props.linkedAccountId);
  isInCardRequestMode = () => Boolean(this.props.cardRequestId);
  isVirtualCardForm = () => this.props.capabilities?.issuing?.virtual;

  isVirtualOrGhostCardType = () => this.props.values.cardType === CardType.Virtual || this.props.values.cardType === CardType.Ghost;

  renderTopGuidanceText() {
    // This text doesn't really make sense in edit mode.
    if (this.props.isInEditMode) {
      return null;
    }

    const { institutionName } = this.props;
    const { assigneeUserIds } = this.props.values;

    return (
      <Grid item xs={12}>
        {assigneeUserIds && assigneeUserIds.length > 1 && (
          <Alert severity="warning" style={{ marginBottom: 0 }}>
            Selected employees have different team fields.
          </Alert>
        )}
        {this.isVirtualCardForm() && (
          <Typography>Assign a new {institutionName} card. Upon selecting Issue Card you will be issuing a new virtual card.</Typography>
        )}
      </Grid>
    );
  }

  handleUserSelect(value?: Partial<IUserExtension> | Partial<IUserExtension>[]) {
    if (Array.isArray(value)) {
      this.onChange({ assigneeUserIds: value.map((x) => x.id || x.email || '') || [] });
    } else {
      this.onChange({ assigneeUserId: value?.id, assigneeEmail: value?.email });
    }
  }

  renderEmployeeField() {
    const {
      usersById,
      userOptions,
      values: { assigneeUserId, assigneeEmail, assigneeUserIds }
    } = this.props;

    const commonProps = {
      id: 'card-issuing-employee-select',
      name: 'card-issuing-employee-select',
      label: 'Employee',
      required: true,
      disabled: this.props.isInEditMode || this.isInCardRequestMode(),
      userOptions
    };

    if (window.FEATURES.accelerated_card_issuing && !this.isManager) {
      const isNewUser = !assigneeUserId && assigneeEmail;
      const isInvitedUser = assigneeUserId && usersById[assigneeUserId] && usersById[assigneeUserId].is_invited;
      const multiple = this.props.multiple;
      const showIssuingAlert =
        (isNewUser || isInvitedUser) &&
        (!window.FEATURES.consolidated_invite_issuing || (window.FEATURES.consolidated_invite_issuing && this.isVirtualOrGhostCardType()));

      return (
        <>
          <Grid item xs={12}>
            <AddableUserAutocomplete
              {...commonProps}
              selectedId={assigneeUserId}
              selectedEmail={assigneeEmail}
              selectedIds={multiple ? assigneeUserIds || [] : []}
              multiple={multiple ? true : false}
              onChange={(value?: Partial<IUserExtension> | Partial<IUserExtension>[]) => this.handleUserSelect(value)}
            />
          </Grid>
          {showIssuingAlert && (
            <Grid item xs={12}>
              <Alert style={{ margin: 0 /* No margin since Grid takes care of spacing */ }} severity="info">
                Card will only be issued once user accepts invite
              </Alert>
            </Grid>
          )}
        </>
      );
    }
    return (
      <Grid item xs={12}>
        <EmployeeSelect
          {...commonProps}
          selectedValue={userOptions.find((x) => x.id === assigneeUserId)}
          onChange={(_event: React.ChangeEvent<{}>, value: IUserExtension) => this.onChange({ assigneeUserId: value?.id })}
        />
      </Grid>
    );
  }

  renderParentCardField() {
    const {
      parentCardOptions,
      values: { parentCardId }
    } = this.props;
    return (
      <Grid item xs={12}>
        <ParentCardSelect
          disabled={this.props.isInEditMode || parentCardOptions.length === 1}
          value={parentCardId || ''}
          parentCards={parentCardOptions}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.onChange({ parentCardId: event.target.value })}
        />
      </Grid>
    );
  }

  renderNameField() {
    const {
      values: { assigneeUserId, assigneeEmail, firstName, lastName, cardName, cardType }
    } = this.props;

    const isNewUser = !assigneeUserId && assigneeEmail;
    const isInvitedUser = assigneeUserId && this.props.usersById[assigneeUserId]?.is_invited;

    const showFirstLastNameFields =
      !this.isVirtualOrGhostCardType() &&
      window.FEATURES.consolidated_invite_issuing &&
      (isNewUser || isInvitedUser) &&
      !this.props.isInEditMode;

    if (showFirstLastNameFields) {
      return (
        <>
          <Grid item xs={12}>
            <TextField
              type="text"
              fullWidth
              required
              name="first-name-input"
              id="first-name-input"
              label="First Name"
              value={firstName || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.onChange({ firstName: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              type="text"
              fullWidth
              required
              name="last-name-input"
              id="last-name-input"
              label="Last Name"
              value={lastName || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.onChange({ lastName: e.target.value })}
            />
          </Grid>
        </>
      );
    }

    return (
      <Grid item xs={12}>
        <TextField
          type="text"
          fullWidth
          required
          name="card-purpose-input"
          id="card-purpose-input"
          label={cardType === CardType.Physical ? 'Card Name' : 'Card Purpose'}
          disabled={this.props.isInEditMode && !this.isVirtualOrGhostCardType()}
          value={cardName || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.onChange({ cardName: e.target.value })}
        />
      </Grid>
    );
  }

  renderBudgetFields() {
    const {
      values: { allowanceAmount, allowanceInterval }
    } = this.props;

    const cardCapabilities = this.cardCapabilities();
    if (!cardCapabilities.allowance.intervalAmount) {
      return null;
    }

    const onIntervalChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      this.onChange({ allowanceInterval: event.target.value as ICardRequestModel['frequency'] });
    };

    return (
      <>
        <Grid item xs={6}>
          <CurrencyInput value={allowanceAmount} onChange={(val) => this.onChange({ allowanceAmount: val })} />
        </Grid>
        <Grid item xs={6}>
          {cardCapabilities.allowance.interval && (
            <CapabilitiesIntervalField value={allowanceInterval} capabilities={cardCapabilities} onChange={onIntervalChange} />
          )}
        </Grid>
      </>
    );
  }

  cardCapabilities = () => (this.isVirtualCardForm() ? this.props.capabilities?.virtualCard : this.props.capabilities?.card);

  renderDateFields() {
    const {
      values: { allowanceStartDate, allowanceEndDate }
    } = this.props;
    const cardCapabilities = this.cardCapabilities();
    const includeStartDate = cardCapabilities?.allowance?.startDate;
    const includeEndDate = cardCapabilities?.allowance?.endDate;

    return (
      (includeStartDate || includeEndDate) && (
        <CancellableDateRange
          includeStartDate={includeStartDate}
          includeEndDate={includeEndDate}
          startDate={allowanceStartDate?.toDate() || null}
          endDate={allowanceEndDate?.toDate() || null}
          onStartChange={(date) => this.onChange({ allowanceStartDate: date ? moment(date) : null })}
          onEndChange={(date) => this.onChange({ allowanceEndDate: date ? moment(date) : null })}
        />
      )
    );
  }

  showTransactionLimitField = () => {
    return this.cardCapabilities()?.allowance?.transactionLimit;
  };

  renderTransactionLimitField = () => {
    const { allowanceAmount, allowanceTransactionLimit } = this.props.values;

    return (
      <Grid item xs={12}>
        <TextField
          type="number"
          fullWidth
          startAdornment="$"
          name="card-limit-input"
          id="card-limit-input"
          label="Transaction Limit"
          onWheel={(e: React.WheelEvent<Element>) => (e.target as HTMLInputElement).blur()}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            this.onChange({ allowanceTransactionLimit: Number(e.target.value) || null })
          }
          value={allowanceTransactionLimit || ''}
          inputProps={{ min: 0, max: allowanceAmount || Infinity }}
        />
      </Grid>
    );
  };

  showDateRangeSection = () => {
    const cardCapabilities = this.cardCapabilities();
    return cardCapabilities?.allowance.startDate || cardCapabilities?.allowance.endDate;
  };

  showVendorRestrictions = () => {
    const cardCapabilities = this.cardCapabilities();
    return cardCapabilities?.allowance?.allowedVendor;
  };

  showMCCSection = () => {
    const cardCapabilities = this.cardCapabilities();
    return cardCapabilities?.allowance?.supportedMerchantGroupRestrictions > 0;
  };

  renderVendorRestrictions = () => {
    const defaultValue = this.props.values.allowanceVendor;

    return (
      <Grid item xs={12}>
        <VendorRestrictionsField defaultValue={defaultValue} onChange={(vendor: string) => this.onChange({ allowanceVendor: vendor })} />
      </Grid>
    );
  };

  renderMCC = () => {
    const defaultValue = this.props.values.allowanceMerchantGroupCategories;

    return (
      <Grid item xs={12}>
        <MerchantCategoryCodesField
          defaultValue={defaultValue}
          onChange={(merchantCategories) => this.onChange({ allowanceMerchantGroupCategories: merchantCategories })}
        />
      </Grid>
    );
  };

  renderAdvancedControls = () => {
    return (
      <Grid item xs={12}>
        <Spacer size={20} />
        <Typography variant="subtitle1">Advanced Controls</Typography>
      </Grid>
    );
  };

  render() {
    const isVirtualCardForm = this.isVirtualCardForm();
    return (
      <Grid container spacing={2}>
        {this.renderTopGuidanceText()}
        {this.renderEmployeeField()}
        {isVirtualCardForm && this.renderParentCardField()}
        {this.renderNameField()}
        {this.renderBudgetFields()}
        {this.renderAdvancedControls()}
        {this.showDateRangeSection() && this.renderDateFields()}
        {this.showTransactionLimitField() && this.renderTransactionLimitField()}
        {this.showVendorRestrictions() && this.renderVendorRestrictions()}
        {this.showMCCSection() && this.renderMCC()}
      </Grid>
    );
  }
}
